import { useEffect, useState } from 'react';
import { ewsDashboardAPI } from '../../services/api';
import { InlineLoader } from '../loading';
import { Modal } from './DashboardPrimitives';
import { EWS_REMARK_MAX_CHARS, remarkBy, remarkWhen, type EwsRemark } from './ews';

/**
 * Dashboard → EWS: what was said to one employee.
 *
 * Every earlier remark, oldest first as a conversation reads, and a box to add one at any
 * time. Remarks are only ever added: there is no edit and no delete. The text is drawn as
 * plain text. Who wrote it and when come from the server, never from this page.
 */

const failureOf = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export default function EwsRemarksModal({ projectId, employee, onAdded, onClose }: {
  projectId: number;
  employee: { code: string; name: string };
  onAdded: (remark: EwsRemark) => void;
  onClose: () => void;
}) {
  const [remarks, setRemarks] = useState<EwsRemark[] | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    ewsDashboardAPI.getRemarks(projectId, { empCode: employee.code })
      .then((res) => { if (alive) setRemarks((res.data?.data?.remarks ?? []) as EwsRemark[]); })
      .catch((err) => { if (alive) setReadError(failureOf(err, 'Remarks could not be read just now. Try again in a minute.')); });
    return () => { alive = false; };
  }, [projectId, employee.code]);

  const save = () => {
    const remark = text.trim();
    if (!remark) {
      setSaveError('Enter what was discussed first.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    ewsDashboardAPI.addRemark(projectId, { empCode: employee.code, remark })
      .then((res) => {
        const saved = res.data?.data?.remark as EwsRemark;
        setRemarks((cur) => [...(cur ?? []), saved]);
        setText('');
        onAdded(saved);
      })
      .catch((err) => setSaveError(failureOf(err, 'The remark could not be saved. Try again in a minute.')))
      .finally(() => setSaving(false));
  };

  return (
    <Modal title={`${employee.name} · remarks`} onClose={onClose} maxWidth="max-w-2xl">
      {readError && <p className="text-sm text-rose-700">{readError}</p>}
      {!readError && remarks === null && <InlineLoader label="Reading the remarks…" />}
      {remarks !== null && remarks.length === 0 && (
        <p className="text-sm text-gray-500">No conversation recorded yet.</p>
      )}
      {remarks !== null && remarks.length > 0 && (
        <ul className="divide-y divide-gray-100" aria-label="Remarks">
          {remarks.map((m) => (
            <li key={m.id} className="py-3">
              <p className="whitespace-pre-wrap break-words text-sm text-gray-900">{m.text}</p>
              <p className="mt-1 text-xs text-gray-500">{remarkBy(m)} · {remarkWhen(m.at)}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 space-y-2">
        <label htmlFor="ews-remark" className="block text-sm font-medium text-gray-700">Add a remark</label>
        <textarea
          id="ews-remark"
          rows={3}
          value={text}
          maxLength={EWS_REMARK_MAX_CHARS}
          onChange={(e) => { setText(e.target.value); setSaveError(null); }}
          placeholder="What was discussed with the employee"
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
        {saveError && <p className="text-sm text-rose-700">{saveError}</p>}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Add remark'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
